<script>
  import { onMount } from "svelte";
  import {
    Page,
    Navbar,
    NavRight,
    Block,
    Card,
    CardContent,
    Button,
    Icon,
    f7,
    f7ready,
  } from "framework7-svelte";
  import { BASE_URL, ENGINE_URL } from "../services/service.config";
  import { GQLNow } from "gql-now";

  export let f7route = undefined;
  export let f7router = undefined;

  let admins = [];
  let searchQuery = "";
  let isLoading = false;
  let errorMessage = null;

  async function fetchAdmins() {
    isLoading = true;
    errorMessage = null;

    try {
      // 1. Try REST backend endpoint
      const res = await fetch(`${ENGINE_URL}/admins`);
      if (res.ok) {
        const data = await res.json();
        if (data && Array.isArray(data.admins)) {
          admins = data.admins;
          isLoading = false;
          return;
        }
      }
    } catch (e) {
      console.warn("REST /admins fetch failed, falling back to directory filter:", e);
    }

    // 2. Fallback: query all users and filter admins
    try {
      const res = await fetch(`${ENGINE_URL}/users`);
      if (res.ok) {
        const data = await res.json();
        const rawUsers = Array.isArray(data) ? data : (data && Array.isArray(data.users) ? data.users : []);
        // In users list, if role is present or check members
        admins = rawUsers.filter((u) => {
          const r = u.roles || [];
          return r.includes("org_admin") || r.includes("admin") || u.is_admin;
        });
      }
    } catch (err) {
      console.error("Failed to load admins list:", err);
      errorMessage = err.message || "Failed to load admin accounts";
    } finally {
      isLoading = false;
    }
  }

  $: filteredAdmins = admins.filter((a) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return (
      (a.fullName || "").toLowerCase().includes(q) ||
      (a.emailAddress || "").toLowerCase().includes(q) ||
      (a.mobile || "").includes(q) ||
      (a.ucc || "").toLowerCase().includes(q)
    );
  });

  onMount(() => {
    f7ready(() => {
      fetchAdmins();
    });
  });
</script>

<Page name="admin-users-list">
  <Navbar title="Administrator Accounts" backLink="Back">
    <NavRight>
      <Button small outline color="blue" onClick={fetchAdmins} disabled={isLoading}>
        {isLoading ? "Refreshing..." : "Refresh"}
      </Button>
    </NavRight>
  </Navbar>

  <Block>
    <!-- Header Stats & Search -->
    <div class="admin-header-strip margin-bottom">
      <div class="display-flex align-items-center gap-12">
        <div class="admin-badge">
          <span class="pulse-dot"></span>
          <strong>{admins.length}</strong> Authorized Administrators
        </div>
      </div>

      <div class="search-box">
        <input
          type="text"
          bind:value={searchQuery}
          placeholder="Search by Name, Email, Mobile, or UCC..."
          class="admin-search-input"
        />
        {#if searchQuery}
          <button class="clear-search-btn" on:click={() => (searchQuery = "")}>✕</button>
        {/if}
      </div>
    </div>

    {#if errorMessage}
      <Card class="bg-color-red text-color-white margin-bottom">
        <CardContent>
          <div class="display-flex justify-content-space-between align-items-center">
            <div>
              <strong>⚠️ Error Loading Administrators</strong>
              <div style="font-size: 13px; margin-top: 4px;">{errorMessage}</div>
            </div>
            <Button fill small color="white" text-color="red" onClick={fetchAdmins}>
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    {/if}

    <!-- Administrators Data Table -->
    <div class="card data-table admin-table-card">
      <div class="card-content" style="padding:0; overflow-x: auto;">
        <table class="admin-table">
          <thead>
            <tr>
              <th class="label-cell">#</th>
              <th class="label-cell">Full Name</th>
              <th class="label-cell">Email Address</th>
              <th class="label-cell">Mobile Number</th>
              <th class="label-cell">Role</th>
              <th class="label-cell">UCC Tag</th>
              <th class="label-cell">Status</th>
            </tr>
          </thead>
          <tbody>
            {#if isLoading && admins.length === 0}
              <tr>
                <td colspan="7" class="empty-state">
                  Loading administrator accounts...
                </td>
              </tr>
            {:else if filteredAdmins.length === 0}
              <tr>
                <td colspan="7" class="empty-state">
                  {searchQuery ? "No matching administrators found." : "No administrator accounts loaded."}
                </td>
              </tr>
            {:else}
              {#each filteredAdmins as admin, idx (admin.user_id || admin._id || admin.mobile || idx)}
                <tr>
                  <td class="label-cell font-mono text-muted">{idx + 1}</td>
                  <td class="label-cell font-bold text-slate">
                    {admin.fullName || "-"}
                  </td>
                  <td class="label-cell font-mono text-cyan">
                    {admin.emailAddress || "-"}
                  </td>
                  <td class="label-cell font-mono text-green">
                    {admin.mobile || "-"}
                  </td>
                  <td class="label-cell">
                    {#each (Array.isArray(admin.roles) ? admin.roles : [admin.roles || "org_admin"]) as role}
                      <span class="badge {role === 'org_admin' ? 'color-purple' : 'color-blue'}" style="font-size: 11px; margin-right: 4px;">
                        {role}
                      </span>
                    {/each}
                  </td>
                  <td class="label-cell font-mono">
                    {#if admin.ucc && admin.ucc !== "-"}
                      <span class="ucc-pill">{admin.ucc}</span>
                    {:else}
                      <span class="text-muted">-</span>
                    {/if}
                  </td>
                  <td class="label-cell">
                    <span class="badge {admin.account_status === 'approved' ? 'color-green' : 'color-orange'}" style="font-size: 11px;">
                      {admin.account_status || "approved"}
                    </span>
                  </td>
                </tr>
              {/each}
            {/if}
          </tbody>
        </table>
      </div>
    </div>
  </Block>
</Page>

<style>
  .admin-header-strip {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 8px;
    padding: 10px 16px;
    gap: 16px;
    flex-wrap: wrap;
  }

  .gap-12 {
    gap: 12px;
  }

  .admin-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(168, 85, 247, 0.15);
    border: 1px solid rgba(168, 85, 247, 0.4);
    color: #c084fc;
    font-size: 13px;
    padding: 4px 10px;
    border-radius: 6px;
  }

  .pulse-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #c084fc;
    display: inline-block;
  }

  .search-box {
    position: relative;
    flex: 1;
    max-width: 380px;
    min-width: 240px;
  }

  .admin-search-input {
    width: 100%;
    background: #0f172a;
    border: 1px solid #475569;
    color: #f8fafc;
    padding: 8px 30px 8px 12px;
    border-radius: 6px;
    font-size: 13px;
    outline: none;
    box-sizing: border-box;
  }
  .admin-search-input:focus {
    border-color: #38bdf8;
  }

  .clear-search-btn {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    font-size: 12px;
    padding: 2px 6px;
  }

  .admin-table-card {
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 8px;
    box-shadow: none;
  }

  .admin-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    color: #f8fafc;
  }

  .admin-table th {
    background: #0f172a;
    color: #94a3b8;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    padding: 10px 14px;
    border-bottom: 1px solid #334155;
    text-align: left;
  }

  .admin-table td {
    padding: 10px 14px;
    border-bottom: 1px solid #334155;
    vertical-align: middle;
  }

  .admin-table tbody tr:hover {
    background: rgba(51, 65, 85, 0.4);
  }

  .font-bold {
    font-weight: 700;
  }

  .font-mono {
    font-family: -apple-system, BlinkMacSystemFont, "SF Mono", "Segoe UI Mono", monospace;
  }

  .text-slate { color: #f1f5f9; }
  .text-cyan { color: #38bdf8; }
  .text-green { color: #4ade80; }
  .text-muted { color: #64748b; }

  .ucc-pill {
    background: rgba(56, 189, 248, 0.15);
    border: 1px solid rgba(56, 189, 248, 0.3);
    color: #38bdf8;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
  }

  .empty-state {
    text-align: center;
    padding: 36px 16px;
    color: #94a3b8;
    font-size: 14px;
  }
</style>
